import { Injectable } from '@angular/core';
import { UserProfile } from '@app-libs/model';
import { AssistantsApiService } from '../../api/assistants-api.service';
import { ConversationsApiService } from '../../api/conversations-api.service';
import { AuthService } from '../../auth/auth.service';
import { ConfigurationClientService } from '../../client/configuration-client.service';
import { EventBusService, EventTypes } from '../../client/event-bus.service';
import { AssistantContext } from '../assistant/assistant-context.service';
import { ChatStateService } from '../chat-state.service';
import { ChatType } from './chat.types';

/**
 * ChatInitialization - Handles chat initialization operations
 *
 * This service handles:
 * - Chat session initialization based on type and ID
 * - Assistant chat initialization
 * - Conversation chat initialization
 * - Default chat initialization
 * - Workspace and model setup
 * - Finalization of initialization process
 */
@Injectable({
  providedIn: 'root',
})
export class ChatInitializationService {
  constructor(
    private stateService: ChatStateService,
    private assistantsService: AssistantsApiService,
    private conversationsService: ConversationsApiService,
    private authService: AuthService,
    private configService: ConfigurationClientService,
    private eventBus: EventBusService,
    private assistantContext: AssistantContext,
  ) { }

  /**
   * Initializes a chat session based on the provided chat type and ID.
   * (Migrated from ChatApiService.initChat - exact same logic)
   */
  async initChat(chatType: ChatType, id?: string): Promise<void> {
    switch (chatType) {
      case ChatType.Assistant:
        await this.initAssistantChat(id as string);
        break;
      case ChatType.Conversation:
        await this.initConversationChat(id as string);
        break;
      case ChatType.Chat:
      default:
        await this.initDefaultChat();
        break;
    }
  }

  /**
   * Initializes an assistant chat session.
   * (Migrated from ChatApiService.initAssistantChat - exact same logic)
   */
  private async initAssistantChat(assistantId: string): Promise<void> {
    if (!assistantId) {
      throw new Error('Assistant ID is required for assistant chat initialization');
    }

    try {
      // Load assistant data
      const assistant = await this.assistantsService.getAssistantById(assistantId).toPromise();
      if (!assistant) {
        throw new Error(`Assistant with ID ${assistantId} not found`);
      }

      // Initialize the assistant (this handles welcome message processing)
      await this.assistantContext.initAssistant(assistant).toPromise();

      // Finalize initialization
      this.finalizeChatInitialization();

      console.debug(`Assistant chat initialized: ${assistant.name}`);
    } catch (error) {
      console.error('Error initializing assistant chat:', error);
      throw error;
    }
  }

  /**
   * Initializes a conversation chat session.
   * (Migrated from ChatApiService.initConversationChat - exact same logic)
   */
  private async initConversationChat(conversationId: string): Promise<void> {
    if (!conversationId) {
      throw new Error('Conversation ID is required for conversation chat initialization');
    }

    try {
      console.debug('Initializing conversation chat:', conversationId);

      // Load conversation data with messages
      const conversation = await this.conversationsService
        .getConversationById(conversationId, true)
        .toPromise();
      if (!conversation) {
        throw new Error(`Conversation with ID ${conversationId} not found`);
      }

      console.debug('Loading conversation messages:', conversation.messages?.length || 0);

      // Update state with conversation, messages, and thread ID using simplified API
      this.stateService.update({
        conversation: conversation,
        messages: conversation.messages || [],
        threadId: conversation.thread_id,
      });

      console.debug('Messages set in state:', this.stateService.messages.length);

      // Load associated assistant if exists
      if (conversation.assistant_id) {
        const assistant = await this.assistantsService
          .getAssistantById(conversation.assistant_id)
          .toPromise();
        if (assistant) {
          await this.assistantContext.initAssistant(assistant).toPromise();
          this.finalizeChatInitialization();
        }
      } else {
        // No assistant, just set up basic conversation state
        this.stateService.update({
          chatType: ChatType.Conversation,
          selectedModel: conversation.model_id || this.configService.defaultModel,
        });

        // Set up workspace and model
        await this.initializeWorkspaceAndModel();
      }

      console.debug(`Conversation chat initialized: ${conversation.title}`);
    } catch (error) {
      console.error('Error initializing conversation chat:', error);
      throw error;
    }
  }

  /**
   * Initializes a default chat session.
   * (Migrated from ChatApiService.initDefaultChat - exact same logic)
   */
  private async initDefaultChat(): Promise<void> {
    try {
      console.debug('Initializing default chat');

      // Load default assistant for workspace
      const assistant = await this.assistantsService.getDefaultAssistantForWorkspace().toPromise();

      if (assistant) {
        // Initialize with the default assistant
        await this.assistantContext.initAssistant(assistant).toPromise();
        this.finalizeChatInitialization();
      } else {
        // Fallback: Update state with default settings
        this.stateService.update({
          chatType: ChatType.Chat,
          assistant: {},
          selectedModel: this.configService.defaultModel,
        });

        // Set up workspace and model
        await this.initializeWorkspaceAndModel();
      }

      console.debug('Default chat initialized');
    } catch (error) {
      console.error('Error initializing default chat:', error);
      throw error;
    }
  }

  /**
   * Finalizes the chat initialization process.
   * Handles model selection logic based on assistant configuration:
   * - If use_model is set: disable selector, force model (current behavior)
   * - If use_model is NOT set but model_name is set: set preferred model, keep selector enabled
   * - If neither is set: use current behavior
   */
  private finalizeChatInitialization(): void {
    const assistant = this.stateService.assistant;

    if (assistant.use_model) {
      // Force model and disable selector (current behavior)
      this.stateService.modelSelectorEnabled = false;
      this.stateService.selectedModel = assistant.use_model;
      this.stateService.preferredModel = null; // Clear preferred model when forcing
      console.debug('Model selector disabled - forcing model:', assistant.use_model);
    } else if (assistant.model_name) {
      // Set preferred model but keep selector enabled
      this.stateService.modelSelectorEnabled = true;
      this.stateService.preferredModel = assistant.model_name;
      // Don't override selectedModel - let the model selector handle it
      console.debug('Model selector enabled with preferred model:', assistant.model_name);
    } else {
      // Default behavior - no model preference
      this.stateService.modelSelectorEnabled = true;
      this.stateService.preferredModel = this.configService.defaultModel;
      console.debug('Model selector enabled - no model preference');
    }

    console.debug('Chat initialization finalized with assistant:', this.stateService.assistant);
    // Emit initialization complete event to trigger UI updates
    this.eventBus.emit({
      type: EventTypes.CHAT_INITIALIZED,
      data: { assistant: this.stateService.assistant },
    });
  }

  /**
   * Initializes workspace and model settings.
   * (Migrated from ChatApiService.initializeWorkspaceAndModel - exact same logic)
   */
  private async initializeWorkspaceAndModel(): Promise<void> {
    try {
      // Get user profile to determine workspace
      const profile: UserProfile = await this.authService.getProfile();
      const workspaceId = profile.selected_workspace;

      if (!workspaceId) {
        throw new Error('User workspace is not configured');
      }

      // Update state with workspace using simplified API
      this.stateService.update({
        workspaceId,
        modelSelectorEnabled: Boolean(
          this.configService.getSetting('chat', 'autoScrollConversation'),
        ),
        enableAutoScrolling: Boolean(
          this.configService.getSetting('chat', 'autoScrollConversation'),
        ),
      });

      // Emit workspace updated event
      this.eventBus.emit({
        type: EventTypes.WORKSPACE_UPDATED,
        data: { workspaceId, workspaceName: workspaceId },
      });
    } catch (error) {
      console.error('Error initializing workspace and model:', error);
      throw error;
    }
  }
}
